"use client";

import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState } from "@bi-studio/shared/components";
import { useForm } from "@mantine/form";
import { useMantineColorScheme } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type {
    ChartUpdate,
    ChartConfig,
    Report,
} from "@bi-studio/shared/lib";
import {
    getChart,
    updateChart,
    getReports,
    executeReport,
    getReport,
} from "@bi-studio/shared/apis";
import { renderChart } from "@/charts";
import { PreviewVariablesModal } from "@/components/reports/modals/PreviewVariablesModal";
import { PREDEFINED_VARIABLES } from "@bi-studio/shared/lib";
import { PageActions } from "@/components/common";
import { ChartEditActionsButton } from "@/components/page-action-buttons";
import { ChartEditView } from "@/components/charts";

export default function EditChartPage() {
    const { colorScheme } = useMantineColorScheme();
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const { id: chartId } = useParams();

    const [reports, setReports] = React.useState<Report[]>([]);
    const [selectedReport, setSelectedReport] = React.useState<Report | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [activeTab, setActiveTab] = React.useState<string | null>("general");

    // Preview state
    const [previewData, setPreviewData] = React.useState<Record<string, unknown>[]>([]);
    const [isLoadingPreview, setIsLoadingPreview] = React.useState(false);
    const [isVariablesModalOpen, setIsVariablesModalOpen] = React.useState(false);
    const [variableValues, setVariableValues] = React.useState<Record<string, string>>({});
    const chartContainerRef = React.useRef<HTMLDivElement>(null);

    const form = useForm<ChartUpdate>({
        initialValues: {
            reportId: "",
            type: "bar",
            config: {
                title: "",
            },
            allowedRoles: [],
        },
        validate: {
            reportId: (value) => (value ? null : "Report is required"),
            config: {
                title: (value) => (value?.trim() ? null : "Chart title is required"),
                xField: (value, values) => (['bar', 'line', 'stacked-line', 'stacked-bar-line', 'area', 'correlation', 'scatter'].includes(values.type!) && !value ? "X Field is required" : null),
                yField: (value, values) => (['bar', 'line', 'correlation', 'scatter'].includes(values.type!) && !value ? "Y Field is required" : null),
                yFields: (value, values) => (['stacked-line', 'area'].includes(values.type!) && (!value || (value as string[]).length === 0) ? "At least one Y Field is required" : null),
                valueFields: (value, values) => (values.type === 'radar' && (!value || (value as string[]).length === 0) ? "At least one Value Field is required" : null),
                valueField: (value, values) => (['pie', 'scorecard', 'calendar', 'gauge', 'map', 'correlation'].includes(values.type!) && !value ? "Value Field is required" : null),
                labelField: (value, values) => (values.type === 'pie' && !value ? "Label Field is required" : null),
                dateField: (value, values) => (values.type === 'calendar' && !value ? "Date Field is required" : null),
                indicatorField: (value, values) => (values.type === 'radar' && !value ? "Indicator Field is required" : null),
                regionField: (value, values) => (values.type === 'map' && !value ? "Region Field is required" : null),
                yFieldBar: (value, values) => (values.type === 'stacked-bar-line' && !value ? "Y Field (Bar) is required" : null),
                yFieldLine: (value, values) => (values.type === 'stacked-bar-line' && !value ? "Y Field (Line) is required" : null),
            },
        },
    });

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    const columnNames = React.useMemo(() => {
        if (selectedReport?.columns && selectedReport.columns.length > 0) {
            return [...new Set(selectedReport.columns.map(c => c.name))];
        }
        if (previewData.length > 0) {
            return Object.keys(previewData[0]);
        }
        return [];
    }, [selectedReport, previewData]);

    // Load initial data
    React.useEffect(() => {
        if (!token || !chartId) return;

        const loadData = async () => {
            try {
                setIsLoading(true);
                const [chartData, reportsData] = await Promise.all([
                    getChart(token, chartId),
                    getReports(token)
                ]);

                setReports(reportsData);
                
                // Handle pie chart field mapping migration if needed (labelField -> categoryField is handled in save/load)
                let initialConfig = { ...chartData.config };
                if (chartData.type === "pie" && "categoryField" in initialConfig) {
                    const { categoryField, ...rest } = initialConfig as any;
                    initialConfig = {
                        ...rest,
                        labelField: categoryField,
                    } as ChartConfig;
                }

                form.setValues({
                    reportId: chartData.reportId,
                    type: chartData.type,
                    config: initialConfig,
                    allowedRoles: chartData.allowedRoles || [],
                });
                form.resetDirty();
            } catch (err) {
                console.error("Failed to load chart data:", err);
                setError(err instanceof Error ? err.message : "Failed to load chart data");
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [token, chartId]);

    // Load report details when report changes
    React.useEffect(() => {
        if (!token || !form.values.reportId) {
            setSelectedReport(null);
            return;
        }

        const loadReportDetails = async () => {
            try {
                const data = await getReport(token, form.values.reportId!);
                setSelectedReport(data);

                // Auto-show variables modal if report has user variables
                const userVariables = (data.variables || []).filter(v => !PREDEFINED_VARIABLES.includes(v.name));
                if (userVariables.length > 0 && Object.keys(variableValues).length === 0) {
                    setIsVariablesModalOpen(true);
                }
            } catch (err) {
                console.error("Failed to load report details:", err);
            }
        };

        void loadReportDetails();
    }, [token, form.values.reportId]);

    const loadPreview = React.useCallback(async (values: Record<string, string> = {}) => {
        if (!token || !form.values.reportId) return;

        try {
            setIsLoadingPreview(true);
            const result = await executeReport(token, form.values.reportId, {
                pagination: { page: 1, pageSize: 0 },
                variables: values,
            });
            setPreviewData(result.rows);
            setVariableValues(values);
            setIsVariablesModalOpen(false);
        } catch (err) {
            console.error("Failed to load preview data:", err);
            setPreviewData([]);
        } finally {
            setIsLoadingPreview(false);
        }
    }, [token, form.values.reportId]);

    React.useEffect(() => {
        if (form.values.reportId) {
            const timeout = setTimeout(() => loadPreview(variableValues), 500);
            return () => clearTimeout(timeout);
        }
    }, [form.values.reportId, variableValues, loadPreview]);

    // Render chart preview
    React.useEffect(() => {
        if (
            !chartContainerRef.current ||
            previewData.length === 0 ||
            !form.values.config ||
            !form.values.reportId ||
            activeTab !== 'preview'
        ) {
            return;
        }

        try {
            const definition = {
                type: form.values.type,
                config: form.values.config as any,
            };

            void renderChart(chartContainerRef.current, previewData, definition as any, {
                width: 600,
                height: 600,
                margin: { top: 32, right: 24, bottom: 48, left: 56 },
            }, (colorScheme === 'dark' ? 'dark' : 'light'));
        } catch (err) {
            console.error("Failed to render chart:", err);
        }
    }, [previewData, form.values.type, form.values.config, form.values.reportId, colorScheme, activeTab]);

    const handleSave = async () => {
        const validation = form.validate();
        if (validation.hasErrors) {
            const errorMessages = Object.values(validation.errors).join(", ");
            
            notifications.show({
                title: "Validation Error",
                message: `Please fix the following: ${errorMessages}`,
                color: "red",
            });
            
            if (validation.errors.reportId || validation.errors['config.title']) {
                setActiveTab("general");
            } else {
                setActiveTab("mapping");
            }
            return;
        }

        if (!token || !chartId) return;

        try {
            setIsSaving(true);
            setError(null);

            // Transform config for pie charts: labelField -> categoryField
            let configToSave = { ...form.values.config };
            if (form.values.type === "pie" && "labelField" in configToSave) {
                const { labelField, ...rest } = configToSave as any;
                configToSave = {
                    ...rest,
                    categoryField: labelField,
                } as ChartConfig;
            }

            const chartUpdateData: ChartUpdate = {
                reportId: form.values.reportId!,
                type: form.values.type!,
                config: configToSave,
                allowedRoles: form.values.allowedRoles,
            };

            await updateChart(token, chartId, chartUpdateData);

            notifications.show({
                title: "Chart Updated",
                message: "Changes have been saved successfully.",
                color: "green",
            });

            // Dispatch event to refresh chart detail if open
            window.dispatchEvent(
                new CustomEvent("chart-updated", { detail: { chartId } })
            );

            navigate(`/charts/${chartId}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update chart");
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(`/charts/${chartId}`);
    };

    const colorPresets = React.useMemo(() => {
        const monochrome = { name: "Default", colors: ["#18181b", "#27272a", "#3f3f46", "#52525b", "#71717a", "#a1a1aa", "#d4d4d8", "#e4e4e7", "#f4f4f5", "#fafafa"] };
        const blueShades = { name: "Blue shades", colors: ["#ECF4FF", "#DCE4F5", "#B9C7E2", "#94A8D0", "#748DC0", "#5F7CB7", "#5474B4", "#44639F", "#3A5890", "#2C4B88"] };
        return [monochrome, blueShades];
    }, [form.values.type]);

    if (authLoading || isLoading) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <LoadingScreen message="Loading chart details..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) return null;

    if (error && !form.values.reportId) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <ErrorState 
                    title="Chart Not Found"
                    message={error || undefined}
                    actionLabel="Back to Folders"
                    onAction={() => navigate('/folders')}
                />
            </AppLayout>
        );
    }

    return (
        <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
            <div className="space-y-6">
                <PageActions 
                    title="Edit Chart" 
                    onBack={handleCancel}
                >
                    <ChartEditActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && activeTab !== 'preview' && (
                    <ErrorState 
                        title="Failed to update chart"
                        message={error || undefined}
                        onAction={() => setError(null)}
                    />
                )}

                <ChartEditView 
                    isSaving={isSaving}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    form={form}
                    reports={reports}
                    columnNames={columnNames}
                    isLoadingPreview={isLoadingPreview}
                    loadPreview={() => loadPreview(variableValues)}
                    previewData={previewData}
                    chartContainerRef={chartContainerRef}
                    colorPresets={colorPresets}
                />

                <PreviewVariablesModal
                    opened={isVariablesModalOpen}
                    onClose={() => setIsVariablesModalOpen(false)}
                    variables={selectedReport?.variables || []}
                    values={variableValues}
                    onValuesChange={setVariableValues}
                    onConfirm={() => loadPreview(variableValues)}
                    dataSourceId={selectedReport?.dataSourceId || ""}
                    loading={isLoadingPreview}
                />
            </div>
        </AppLayout>
    );
}
